import type { Messages } from '../messages.js'

/**
 * English, and the reference every other locale is checked against.
 *
 * A few of these are load-bearing rather than decorative. `noSuchLetterUp` has to be loud,
 * because a player typing ALIAS cannot see how many A tiles the board holds, and silently
 * building ALIS is how a word gets submitted that nobody meant. `hiddenWhilePaused` is what
 * a screen reader says instead of reading out a concealed board.
 */
export const en: Messages = {
  tag: 'en',

  readingDictionary: 'Reading the dictionary…',
  noWordList: 'No word list for "{language}". Build one with:  pnpm dictionary build',
  emptyWordList: 'The word list for "{language}" is empty.',

  flips: 'flips',
  score: 'score',
  words: 'words',
  round: 'round',
  ticksLeftLabel: 'Ticks left in this round',
  typeAWord: 'type a word',
  tapPrompt: 'tap letters to select/unselect, then {action}',

  boardOfTiles: 'Board of {n} tiles',
  faceDown: 'face down',
  wildCard: 'wild card',
  wildKey: 'any letter',
  letterReplaced: '{from} became {to}',
  letterSwap: 'LETTER SWAP!',
  spentTile: 'spent tile',
  hiddenWhilePaused: 'hidden while paused',
  letterInWord: '{letter}, letter {position} of the word',

  completeWord: 'Complete word',

  completeShort: 'Complete',
  reset: 'Reset',
  pause: 'Pause',
  resume: 'Resume',
  newGame: 'New game',
  paused: 'Paused',
  outOfFlips: 'Out of flips',
  finalResult: '{score} points from {words} over {rounds}',
  playAgain: 'Play again',
  share: 'Share',
  shareCopied: 'Copied.',
  shareSelect: 'Copy this:',

  lettersSelect: 'letters select',
  keysWild: 'taken when you type a letter no tile is showing',
  clearsEvery: 'clears all selected {letter}s',
  undoLastLetter: 'undo last letter',
  noWordsYet: 'No words yet.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'already found',
  reasonTooShort: 'too short',
  reasonNotAWord: 'not a word',
  reasonAllFound: 'you already have them all',
  noSuchLetterUp: 'no {letter} up',
  nothingUp: 'not up',
  letterHidden: 'a letter hid',
  shuffled: 'shuffled',
  shuffledAndBilled: 'shuffled, billed {flips} unused flips',
  tooFewLetters: 'Too few letters remaining',

  gameLanguage: 'language',
  interfaceLanguage: 'interface',
  dictionarySize: '{common} common of {full} words',
  filterLanguages: 'Search languages',
  noMatches: 'No matches',

  nerdMode: 'nerd mode',
  rules: 'Rules',
  difficulty: 'difficulty',
  difficultyNames: { easy: 'easy', medium: 'medium', hard: 'hard', insane: 'insane' },
  pausedNoBoard: 'This game will not appear on a leaderboard.',
  about: 'About',
  themeLabel: 'theme',
  themeNames: { traditional: 'traditional', light: 'light', contrast: 'high contrast' },
  tiles: 'tiles (N)',
  secondsPerTick: 'seconds / tick',
  holdTicks: 'hold ticks',
  minWord: 'min word',
  startingFlips: 'starting flips',
  wildChance: 'wild card chance',
  replaceChance: 'letter swap chance',
  hideChance: 'letter hide chance',
  wordCompleteMode: 'word complete',
  wordCompleteNames: { shuffle: 'shuffle', spend: 'spend', keep: 'keep' },
  flipEconomy: 'flip economy',
  flipEconomyNames: {
    none: 'none',
    perLetter: 'per letter',
    fibonacci: 'fibonacci',
    overMinimum: 'over minimum',
  },
  repeatedLetterKey: 'repeated letter key',
  keySchemeNames: { cycle: 'cycle', advance: 'advance' },
  keySchemeHelp: {
    cycle:
      'A takes the next unused A, and once they are all in the word, clears them. ' +
      'Shift+A clears them too.',
    advance: 'A takes the next unused A. Shift+A clears every A in the word.',
  },

  whatThatMeans: 'What that means',
  factRound: 'round',
  factWholeBoardUp: 'whole board up for',
  factRoundCosts: 'a round costs',
  factFlipsBuy: 'starting flips buy',
  factThisBoard: 'this board',
  factBoardHadToAdmit: 'board had to admit',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, longest {longest}',
  wordsIncludingOneOf: '{words} incl. one of {ceiling}',
  scorelessRounds: '{rounds} scoreless',

  whatAWordPays: 'What a word pays',
  columnLetters: 'letters',
  columnCost: 'cost',
  columnPoints: 'points',
  columnFlips: 'flips',
  columnNet: 'net',

  canonicalRules: 'Canonical {difficulty} rules.',
  customRules: 'Changed from the preset. Scores under custom rules will not be ranked.',
  applyAndStart: 'Apply and start a new game',
  changesNextGame: 'Changes take effect on the next game.',
  presets: 'Presets:',

  start: 'Start',
  restart: 'Restart',
  quit: 'Quit',
  quitTitle: 'Quit this game?',
  restartTitle: 'Restart this game?',
  restartConfirm: 'Restart',
  quitConfirm: 'Quit',
  keepPlaying: 'Keep playing',
  personalBest: 'Your best games',
  newPersonalBest: 'A new personal best.',
  columnRank: '#',
  notRanked: 'Custom rules, so this game is not ranked.',
  notRankedPaused: 'The clock stopped, so this game is not ranked.',
  rankOfTotal: '{rank} of {total}',

  howToPlay: 'How to play',

  backToGame: 'Back to the game',
  welcomeTitle: 'Welcome to Blinkered',
  tutorialSkip: 'Skip',
  tutorialNext: 'Next',
  tutorialBack: 'Back',
  tutorialStart: 'Start playing',
  tutorialHideAgain: "Don't show this again",
  tutorialProgress: '{n} of {total}',
  tutorialSkipTitle: 'Skip the tour?',
  tutGoalTitle: 'Blinkered is a race',
  tutGoalBody:
    'You start the game with a fixed set of flips, and every tile that turns over costs one flip. When the flips run out, the game is over. Making words earns flips, and the longer the word, the more flips you earn.',
  tutSpentBody:
    'Letters you use in a word turn face down until the next round. Only a swap changes which letters are on the board.',
  tutChangeTitle: 'Letters can change',
  tutSameLetters:
    "Spotted a word you didn't get to play? The same letters come back next round, shuffled into new places.",
  tutPickLetters: 'Tap the letters you want, in order, to form a word.',
  tutMoreTurn: 'Tiles keep turning while you think, so a better letter may still be coming.',
  tutTapBack:
    'Tapped one you did not want? Tap it again to give it back. Any of them, not just the last.',
  tutComplete: 'Press Complete when the word is ready.',
  htBoardTitle: 'The board',
  htBoardBody:
    'Tiles turn face up one at a time, in random order. Exposed tiles can be used to form words.',
  htWordsTitle: 'The words',
  htWordsBody:
    'Spell a word from the tiles that are showing by typing or clicking the letters in order.',
  htFlipsTitle: 'The flips',
  htFlipsBody:
    'Every tile that turns costs a flip. A completed word adds flips back to your total, and longer words pay more. When the flips run out, the game is over.',
  htRoundTitle: 'The round',
  htRoundBody:
    'When the last tile of a round turns, the whole board holds for a moment. Then the tiles are turned over and shuffled, and a new round begins.',
  htLanguagesTitle: 'The languages',
  htLanguagesBody:
    '{n} of them. Every board can be solved from words people actually use. An unusual word still scores, if the dictionary knows it.',
  htKeysTitle: 'The keyboard',
  htWildTitle: 'Wild cards',
  htWildBody:
    'Sometimes a wild card will appear instead of a letter. A wild card counts as any letter that makes a valid word. A previously-completed word does not count.',
  htHideTitle: 'Hiding letters',
  htHideBody:
    'On the harder levels a letter that is showing can turn back over by itself, and will come back later in the round. The timer gains a tick for it, shown in red. Already-selected letters will not do this.',
  tutHideBody:
    'On the harder levels a letter that is showing can turn back over by itself, and will come back later in the round. Already-selected letters will not do this.',
  htSwapTitle: 'Changing letters',
  htSwapBody:
    'Sometimes, between rounds, one letter gets replaced with a different one. You will see which letter was removed and which was added.',
  htLevelsTitle: 'The levels',
  htLevelEasy:
    'The same twelve letters all game, so you can learn them and carry a list of words in your head. Tiles turn slowly, and the full board stays in view long enough to finish your word selections.',
  htLevelMedium:
    'A letter changes now and then, so it becomes harder to keep track of words you want to play in the future. Less time to look, and less time to think.',
  htLevelHard:
    'Three-letter words stop counting, and a letter changes about every other round. The board is barely showing before it shuffles.',
  htLevelInsane:
    'Everything at once, at speed. The board shuffles almost immediately after the last flip.',
  htTouchTitle: 'The touchscreen',
  htTouchBody:
    'Tap a tile that is showing to take its letter. Tap any letter you have taken to give it back. Complete and Reset sit under the board.',

  accountTitle: 'Your account',
  menuProfile: 'My profile',
  menuGames: 'My games',
  menuPublicPage: 'My public page',
  menuSignedInAs: 'Signed in as',
  signIn: 'Sign in',
  signOut: 'Sign out',
  signInTitle: 'Sign in or sign up',
  signInLead: 'An account keeps your games, on every device you play on.',
  signInKeepGame: 'Sign in to keep this game, and every one after it.',
  continueWithApple: 'Continue with Apple',
  continueWithGoogle: 'Continue with Google',
  signInOr: 'or',
  emailLabel: 'Email',
  codeLabel: 'Code',
  emailMeACode: 'Email me a code',
  working: 'Working…',
  sendNewCode: 'Send a new code',
  codeSent: 'Check your email. The code lasts 10 minutes.',
  notNow: 'Not now',
  badEmail: 'That does not look like an email address.',
  badCode: 'That code did not work. Codes expire after 10 minutes and can only be used once.',
  serverBusy: 'Could not reach the server. Try again in a moment.',
  serverDown: 'Could not reach the server.',
  offline: 'Offline',
  leaderboardTitle: 'Leaderboard',
  leaderboardEmpty: 'No games on this board yet. Be the first.',
  leaderboardWouldBe: 'Where this game would rank',
  leaderboardThisGame: 'This game',
  leaderboardRanksHere: 'Where this game ranks',
  leaderboardOutside: 'Outside the top five.',
  tutAccountTitle: 'Keep your scores',
  tutAccountBody:
    'An account saves your games, puts your scores on the leaderboard, and keeps your profile.',
  ssoCancelled: 'Signing in was cancelled.',
  ssoExpired: 'That took too long. Try signing in again.',
  ssoNoUsername: 'Could not finish creating an account. Try again in a moment.',
  ssoFailed: 'That did not work. Try again, or use your email.',
  tabProfile: 'Profile',
  countryAny: 'Not saying',
  findCountry: 'Search countries',
  usernameLabel: 'Username',
  bioLabel: 'Bio',
  countryLabel: 'Country',
  bioHint: 'No links. {left}/{max}',
  save: 'Save',
  saving: 'Saving…',
  saved: 'Saved.',
  nameTaken: 'Somebody already has that name.',
  nameTooShort: 'A bit longer, please.',
  nameTooLong: 'That is too long.',
  nameBadCharacters: 'Letters, digits, hyphens and underscores only.',
  nameBadEdges: 'It has to start and end with a letter or a digit.',
  nameMixedScripts: 'Pick one alphabet and stay in it.',
  nameReserved: 'That looks like a name we hand out. Choose another.',
  nameUnusable: 'That name cannot be used.',
  bioTooLong: 'Bios are {max} characters or fewer.',
  bioHasLink: 'Links are not allowed in a bio.',
  bioHasControl: 'That contains characters a bio cannot hold.',
  bioUnusable: 'That bio cannot be used.',
  gamesHeading: 'Games',
  gamesEmpty: 'Nothing here yet. Games you play while signed in will show up here.',
  gamesEmptyShort: 'Nothing here yet.',
  gamesLoading: 'Reading your games…',
  theirGamesLoading: 'Reading their games…',
  profileLoading: 'Reading the profile…',
  playerNotFound: 'There is nobody here by that name.',
  columnWhen: 'When',
  columnRounds: 'rounds',
  columnGame: 'Game',
  gameLoading: 'Reading the game…',
  gameUnreadable: 'That game could not be read.',
  gameNotRanked: 'Played on edited rules, so this game is not ranked.',
  detailNotKept: 'The words and boards for this game are no longer kept.',
  openingBoard: 'The opening board',
  roundByRound: 'Round by round',
  roundNothing: 'nothing found',
  changeReplaced: '{letter} replaced in slot {slot}',
  changeWildOn: '{letter} wild card in slot {slot}',
  changeWildOff: '{letter} card gone from slot {slot}',
  keepThisGame: 'Keep this game',
  savedToYourGames: 'Saved to your games.',

  reportThis: 'Report',
  reportHeading: 'Report this',
  reportTheName: 'The name',
  reportTheBio: 'The bio',
  reportTheScore: 'The score',
  reportWhy: 'What is wrong with it? Optional.',
  reportSend: 'Send report',
  reportSent: 'Thank you. Somebody will look at it.',
  reportSignIn: 'Sign in to report something.',
  reportGone: 'That is not there any more.',
  reportCancel: 'Cancel',

  deleteAccount: 'Delete my account',
  deleteAccountWhat:
    'This removes your profile and every game you have kept, for good. It cannot be undone.',
  deleteAccountAsk: 'Send me a code',
  deleteConfirm: 'Delete it for good',
  deleteGone: 'Your account is gone. Thank you for playing.',
  deleteNoAddress:
    'There is no confirmed address on this account, so a deletion cannot be checked. Get in touch and we will do it by hand.',

  rulesetFacts: 'Words of {letters} · a new tile every {seconds}',
  plurals: {
    words: { one: '{n} word', other: '{n} words' },
    rounds: { one: '{n} round', other: '{n} rounds' },
    flips: { one: '{n} flip', other: '{n} flips' },
    ticks: { one: '{n} tick', other: '{n} ticks' },
    points: { one: '{n} point', other: '{n} points' },
    minLetters: { one: '{n}+ letter', other: '{n}+ letters' },
    seconds: { one: '{n} second', other: '{n} seconds' },
  },
}
